import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Modal } from "antd";
import { Icon } from "../components/ui";
import { useNavigate } from "react-router-dom";
import { ApiResponse, LoginForm, LoginResponseData } from "../types";
import { AuthService } from "../services";
import TokenManager from "../utils/tokenManager";
import ErrorHandler from "../utils/errorHandler";
import "../styles/Login.scss";

const Login: React.FC = () => {
  type LoginMode = "sms" | "password";
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState<LoginMode>("sms");
  const [passwordResetMode, setPasswordResetMode] = useState(false);
  const [requiresInvite, setRequiresInvite] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const navigate = useNavigate();

  // 图片验证码相关状态
  const [captchaId, setCaptchaId] = useState<string>("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaModalVisible, setCaptchaModalVisible] = useState(false);
  const [captchaCode, setCaptchaCode] = useState<string>("");
  const [svgData, setSvgData] = useState<string>("");

  const finishLogin = (response: ApiResponse<LoginResponseData>) => {
    if (response.code === 0 && response.data) {
      TokenManager.setToken(response.data.accessToken);
      message.success("登录成功！");

      const userInfo = TokenManager.getUserFromToken(response.data.accessToken);
      if (userInfo?.roles?.includes("admin")) {
        navigate("/users");
      } else {
        navigate("/token-records");
      }
      return true;
    }

    message.error(response.msg || "登录失败");
    return false;
  };

  const resetVerification = () => {
    setRequiresInvite(false);
    setCountdown(0);
    form.resetFields(["code", "inviteCode", "captchaCode"]);
    setCaptchaId("");
    setCaptchaCode("");
    setSvgData("");
    setCaptchaModalVisible(false);
  };

  const handleModeChange = (mode: LoginMode) => {
    if (mode === loginMode) return;
    resetVerification();
    setLoginMode(mode);
    setPasswordResetMode(false);
    form.resetFields(["password", "confirmPassword"]);
  };

  // 加载图片验证码
  const loadCaptcha = async (phone?: string) => {
    try {
      setCaptchaLoading(true);
      const phoneNumber = phone || form.getFieldValue("phoneNumber");

      if (!phoneNumber || !/^1[3-9]\d{9}$/.test(phoneNumber)) {
        message.error("请先输入正确的手机号码");
        return false;
      }

      // 调用验证码 API
      const response = await AuthService.getCaptcha(phoneNumber, "image");

      if (response.code === 0 && response.data) {
        // 从响应中获取验证码信息
        const { captchaId: id, svgData } = response.data;

        if (id && svgData) {
          setCaptchaId(id);
          setSvgData(svgData);
          return true;
        } else {
          message.error("获取验证码失败");
          return false;
        }
      } else {
        message.error(response.msg || "获取验证码失败");
        return false;
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
      return false;
    } finally {
      setCaptchaLoading(false);
    }
  };

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 打开验证码弹窗
  const showCaptchaModal = async () => {
    try {
      // 验证手机号
      await form.validateFields(["phoneNumber"]);
      const success = await loadCaptcha();
      if (success) {
        setCaptchaModalVisible(true);
        setCaptchaCode(""); // 清空之前输入的验证码
      }
    } catch (error) {
      // 表单验证失败
      console.error("表单验证失败:", error);
    }
  };

  // 刷新验证码
  const refreshCaptcha = async () => {
    setCaptchaCode(""); // 清空输入的验证码
    return loadCaptcha();
  };

  // 发送验证码
  const handleSendCode = async () => {
    if (!captchaId || !captchaCode) {
      message.error("请先完成图片验证码验证");
      return;
    }

    try {
      const phoneNumber = form.getFieldValue("phoneNumber");
      setSendingCode(true);

      // 调用发送验证码 API
      const response = await AuthService.sendVerificationCode({
        phoneNumber,
        captchaId,
        captchaCode,
      });

      if (response.code === 0) {
        message.success("验证码已发送到您的手机");
        setCountdown(60);
        // New administrators register on first login but do not need an invite.
        const inviteNeeded = !passwordResetMode &&
          (response.data?.requiresInvite ?? response.data?.isNewUser === true);
        setRequiresInvite(inviteNeeded);
        if (!inviteNeeded) form.setFieldValue("inviteCode", undefined);
        setCaptchaModalVisible(false); // 关闭验证码弹窗
      } else {
        message.error(response.msg || "发送验证码失败");
        // 刷新验证码
        refreshCaptcha();
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
      // 刷新验证码
      refreshCaptcha();
    } finally {
      setSendingCode(false);
    }
  };

  // 密码登录在提交前复用图片验证码弹窗，避免密码登录绕过现有风控。
  const handlePasswordLogin = async () => {
    if (!captchaId || !captchaCode) {
      message.error("请先完成图片验证码验证");
      return;
    }

    const phoneNumber = form.getFieldValue("phoneNumber");
    const password = form.getFieldValue("password");
    setLoading(true);

    try {
      const response = await AuthService.passwordLogin({
        phoneNumber,
        password,
        captchaId,
        captchaCode,
      });

      if (finishLogin(response)) {
        setCaptchaModalVisible(false);
      } else {
        await refreshCaptcha();
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
      await refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaConfirm = () => {
    if (loginMode === "password" && !passwordResetMode) {
      return handlePasswordLogin();
    }
    return handleSendCode();
  };

  const handlePasswordReset = async () => {
    try {
      const values = await form.validateFields(["phoneNumber", "code", "password", "confirmPassword"]);
      if (!captchaId || !captchaCode) {
        message.error("请先获取并完成图片验证码");
        return;
      }
      setLoading(true);
      const response = await AuthService.resetPassword({
        phoneNumber: values.phoneNumber,
        code: values.code,
        captchaId,
        captchaCode,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      if (response.code === 0) {
        message.success("密码已重置，请使用新密码登录");
        form.resetFields(["code", "password", "confirmPassword"]);
        setPasswordResetMode(false);
        setCaptchaId("");
        setCaptchaCode("");
        setSvgData("");
        setCountdown(0);
      } else {
        message.error(response.msg || "密码重置失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 处理登录
  const handleLogin = async (formData: LoginForm & { password?: string }) => {
    if (loginMode === "password") {
      if (passwordResetMode) {
        await handlePasswordReset();
        return;
      }
      await showCaptchaModal();
      return;
    }

    setLoading(true);

    try {
      // 短信登录以一次性短信验证码为准，图片验证码只在发送验证码时校验，这里不再需要
      const response = await AuthService.login({
        phoneNumber: formData.phoneNumber,
        code: formData.code,
        ...(requiresInvite ? { inviteCode: formData.inviteCode?.trim() } : {}),
      });

      if (finishLogin(response)) {
        return;
      } else {
        // 刷新验证码
        await refreshCaptcha();
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
      // 刷新验证码
      await refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const phoneValue: string = Form.useWatch("phoneNumber", form) || "";
  const maskedPhone = /^1[3-9]\d{9}$/.test(phoneValue) ? `${phoneValue.slice(0, 3)}****${phoneValue.slice(7)}` : "";
  const busy = loading || sendingCode || captchaLoading;
  const captchaConfirmLabel = loginMode === "password" && !passwordResetMode ? "确认并登录" : "确认并发送验证码";

  return (
    <div className="login-page">
      <section className="login-story" aria-label="多多拼介绍">
        <img className="login-story__art" src="/illustrations/operations-paper-1600.jpg" alt="" />
        <a className="login-story__brand" href="/" aria-label="返回多多拼首页">
          <img src="/brand/duoduopin-mark-256.png" alt="" width={36} height={36} />
          <span>多多拼</span>
        </a>
        <div className="login-story__copy">
          <h1>把每一次商品运营，<br />变成清楚的下一步。</h1>
          <p>账号、店铺、任务和算力在同一个工作台里流动。登录后，从今天最需要处理的动作开始。</p>
        </div>
        <ol className="login-story__steps">
          <li><span className="mono">01</span>连接店铺</li>
          <li><span className="mono">02</span>编排任务</li>
          <li><span className="mono">03</span>看见结果</li>
        </ol>
      </section>

      <section className="login-panel">
        <a className="login-panel__back" href="/"><Icon name="arrowL" size={15} />返回首页</a>
        <div className="login-panel__inner">
          <div className="login-panel__mobile-brand"><img src="/brand/duoduopin-mark-256.png" alt="" width={32} height={32} /><strong>多多拼</strong></div>
          <header className="login-panel__head">
            <h2>{passwordResetMode ? "重置密码" : "欢迎回来"}</h2>
            <p>{passwordResetMode ? "验证手机号后设置新的登录密码" : "使用管理员或团队手机号登录运营工作台"}</p>
          </header>

          <div className="login-mode" role="tablist" aria-label="登录方式">
            <button type="button" role="tab" aria-selected={loginMode === "sms"} disabled={busy} className={loginMode === "sms" ? "is-active" : ""} onClick={() => handleModeChange("sms")}>
              短信登录
            </button>
            <button type="button" role="tab" aria-selected={loginMode === "password"} disabled={busy} className={loginMode === "password" ? "is-active" : ""} onClick={() => handleModeChange("password")}>
              密码登录
            </button>
          </div>

          <Form form={form} layout="vertical" onFinish={handleLogin} onValuesChange={(changed) => { if ("phoneNumber" in changed) resetVerification(); }} autoComplete="off" className="login-form" requiredMark={false}>
            <Form.Item name="phoneNumber" label="手机号码" rules={[
              { required: true, message: "请输入手机号码" },
              { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号码" },
            ]}>
              <Input prefix={<Icon name="phone" />} placeholder="请输入手机号码" size="large" inputMode="numeric" maxLength={11} disabled={busy} />
            </Form.Item>
            {loginMode === "sms" || passwordResetMode ? (
              <Form.Item
                label="短信验证码"
                extra={countdown > 0 && maskedPhone ? <span className="login-form__sent"><Icon name="check" size={14} strokeWidth={2} />验证码已发送至 {maskedPhone}，5 分钟内有效</span> : undefined}
              >
                <div className="code-input-group">
                  <Form.Item name="code" noStyle rules={[{ required: true, message: "请输入验证码" }, { len: 6, message: "验证码为6位数字" }]}>
                    <Input prefix={<Icon name="message" />} placeholder="请输入 6 位验证码" size="large" maxLength={6} inputMode="numeric" />
                  </Form.Item>
                  <Button size="large" onClick={showCaptchaModal} loading={sendingCode} disabled={countdown > 0} className="send-code-btn">
                    {countdown > 0 ? `${countdown}s 后重发` : "发送验证码"}
                  </Button>
                </div>
              </Form.Item>
            ) : (
              <Form.Item name="password" label="登录密码" rules={[{ required: true, message: "请输入登录密码" }]}>
                <Input.Password prefix={<Icon name="lock" />} placeholder="请输入登录密码" size="large" />
              </Form.Item>
            )}
            {loginMode === "sms" && requiresInvite && (
              <Form.Item
                name="inviteCode"
                label="组织邀请码"
                preserve={false}
                extra="该手机号首次登录，需要组织邀请码完成注册"
                rules={[{ required: true, whitespace: true, message: "新用户注册需要邀请码" }]}
              >
                <Input prefix={<Icon name="key" />} placeholder="请输入组织邀请码" size="large" autoFocus />
              </Form.Item>
            )}
            {loginMode === "password" && passwordResetMode && (
              <>
                <Form.Item name="password" label="新密码" rules={[{ required: true, min: 8, message: "密码至少需要8位" }]}>
                  <Input.Password prefix={<Icon name="lock" />} placeholder="至少 8 位" size="large" />
                </Form.Item>
                <Form.Item name="confirmPassword" label="确认新密码" dependencies={["password"]} rules={[{ required: true, message: "请再次输入密码" }, ({ getFieldValue }) => ({ validator(_, value) { return !value || getFieldValue("password") === value ? Promise.resolve() : Promise.reject(new Error("两次密码不一致")); } })]}>
                  <Input.Password prefix={<Icon name="lock" />} placeholder="请再次输入新密码" size="large" />
                </Form.Item>
              </>
            )}
            {loginMode === "password" && (
              <div className="password-actions">
                <button type="button" disabled={busy} onClick={() => { resetVerification(); form.resetFields(["password", "confirmPassword"]); setPasswordResetMode(!passwordResetMode); }}>
                  {passwordResetMode ? "返回密码登录" : "忘记密码？短信重置"}
                </button>
              </div>
            )}
            <Form.Item className="login-form__submit">
              <Button type="primary" htmlType="submit" size="large" loading={loading} block>
                {loginMode === "sms" ? "进入工作台" : passwordResetMode ? "重置密码" : "验证并登录"}
              </Button>
            </Form.Item>
          </Form>
          <p className="login-panel__agree">登录即表示同意<a href="/user-privacy-agreement">《用户协议与隐私说明》</a></p>
          <p className="login-panel__foot"><Icon name="lock" size={13} />{loginMode === "sms" ? "验证码登录，不保存密码" : "密码通过安全通道传输"}</p>
        </div>
      </section>

      {/* 图片验证码弹窗 */}
      <Modal
        title="请完成图片验证"
        open={captchaModalVisible}
        width={400}
        rootClassName="ddp-modal"
        centered
        onCancel={() => setCaptchaModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setCaptchaModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loginMode === "password" && !passwordResetMode ? loading : sendingCode}
            onClick={handleCaptchaConfirm}
            disabled={!captchaCode}
          >
            {captchaConfirmLabel}
          </Button>,
        ]}
      >
        <div className="captcha-modal-content">
          <p className="captcha-modal-content__desc">为了账户安全，请输入下图中的字符。</p>
          <button type="button" className="captcha-image-container" onClick={refreshCaptcha} aria-label="刷新图片验证码">
            {captchaLoading ? (
              <span className="captcha-loading">加载中...</span>
            ) : svgData ? (
              <span className="captcha-svg" dangerouslySetInnerHTML={{ __html: svgData }} />
            ) : (
              <span className="captcha-error">验证码加载失败，请点击重试</span>
            )}
            <span className="captcha-tip"><Icon name="refresh" size={13} />换一张</span>
          </button>
          <Input
            prefix={<Icon name="shieldCheck" />}
            placeholder="请输入图片验证码"
            value={captchaCode}
            onChange={(e) => setCaptchaCode(e.target.value)}
            onPressEnter={() => { if (captchaCode) handleCaptchaConfirm(); }}
            maxLength={6}
            size="large"
            className="captcha-input mono"
            autoFocus
            aria-label="图片验证码"
          />
        </div>
      </Modal>
    </div>
  );
};

export default Login;
